import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, AlertTriangle, XCircle, Ruler, Smartphone } from "lucide-react";
import { FitPlan } from "@/components/FitPlan";
import {
  BODY_DEFAULTS,
  BODY_LABEL,
  DEFAULT_SITE,
  evaluateFit,
  mirrorWidth,
  verdict,
  type BodyType,
  type VehicleDims,
} from "@/lib/vehicle-fit";

const CHIPS: BodyType[] = ["van", "halvintegrerad", "alkov", "husvagn"];

const TYPICAL_LENGTH: Record<BodyType, number> = {
  integrerad: 7.6,
  halvintegrerad: 7.4,
  alkov: 7.0,
  van: 5.99,
  husvagn: 7.2,
};

const ICON = {
  ok: <CheckCircle2 size={18} className="text-emerald-400" />,
  warn: <AlertTriangle size={18} className="text-amber-400" />,
  fail: <XCircle size={18} className="text-red-400" />,
};

const fmt = (v: number) => v.toFixed(1).replace(".", ",");

/**
 * Interaktiv teaser för "Passar den hos mig?" – två reglage ger direkt svar
 * på startsidan, och länken tar med de inmatade måtten till fullversionen.
 */
export function FitTeaser() {
  const [bodyType, setBodyType] = useState<BodyType>("halvintegrerad");
  const [driveWidth, setDriveWidth] = useState(3.4);
  const [clearHeight, setClearHeight] = useState(0);

  const dims: VehicleDims = useMemo(
    () => ({ ...BODY_DEFAULTS[bodyType], bodyType, length: TYPICAL_LENGTH[bodyType] }),
    [bodyType],
  );
  const site = useMemo(
    () => ({ ...DEFAULT_SITE, driveWidth, clearHeight }),
    [driveWidth, clearHeight],
  );
  const checks = useMemo(() => evaluateFit(dims, site), [dims, site]);
  const total = verdict(checks);
  const highlights = useMemo(() => {
    const bad = checks.filter((c) => c.level !== "ok");
    return (bad.length ? bad : checks).slice(0, 3);
  }, [checks]);

  return (
    <section className="relative overflow-hidden border-y border-border bg-foreground text-background">
      <div className="pointer-events-none absolute inset-0 opacity-[0.13] [background:radial-gradient(circle_at_15%_20%,var(--accent)_0%,transparent_45%),radial-gradient(circle_at_85%_80%,var(--accent)_0%,transparent_40%)]" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(var(--accent) 1px, transparent 1px), linear-gradient(90deg, var(--accent) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="container-edge relative py-14 md:py-20">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)] lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
              <Ruler size={12} /> Gratis verktyg
            </span>
            <h2 className="mt-5 font-display text-4xl leading-[0.95] md:text-6xl">
              Får den plats{" "}
              <span className="font-italic-display headline-accent">hemma hos dig</span>?
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-background/75 md:text-lg">
              Dra i reglagen och se svaret direkt. Vi räknar med utfällda speglar, dörrsvep,
              markis och snövallar – samma mått vi använder när vi levererar fordon i Norrbotten.
            </p>

            <div className="mt-8 flex flex-wrap gap-2">
              {CHIPS.map((b) => (
                <button
                  key={b}
                  onClick={() => setBodyType(b)}
                  aria-pressed={bodyType === b}
                  className={`rounded-full border px-4 py-2 text-sm transition ${
                    bodyType === b
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-background/25 text-background/80 hover:border-background/50"
                  }`}
                >
                  {BODY_LABEL[b]}
                </button>
              ))}
            </div>

            <div className="mt-7 space-y-6">
              <label className="block">
                <span className="flex items-baseline justify-between text-sm font-medium">
                  Uppfartens bredd
                  <span className="font-display text-xl text-accent">{fmt(driveWidth)} m</span>
                </span>
                <input
                  type="range"
                  min={2}
                  max={7}
                  step={0.1}
                  value={driveWidth}
                  onChange={(e) => setDriveWidth(Number(e.target.value))}
                  aria-label="Uppfartens bredd i meter"
                  className="mt-2 w-full [accent-color:var(--accent)]"
                />
              </label>
              <label className="block">
                <span className="flex items-baseline justify-between text-sm font-medium">
                  Fri höjd (port, tak, grenar)
                  <span className="font-display text-xl text-accent">
                    {clearHeight === 0 ? "fri himmel" : `${fmt(clearHeight)} m`}
                  </span>
                </span>
                <input
                  type="range"
                  min={0}
                  max={4.5}
                  step={0.05}
                  value={clearHeight}
                  onChange={(e) => setClearHeight(Number(e.target.value))}
                  aria-label="Fri höjd i meter, 0 betyder ingen begränsning"
                  className="mt-2 w-full [accent-color:var(--accent)]"
                />
              </label>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/verktyg/passar-den-hos-mig"
                search={{
                  b: driveWidth,
                  h: clearHeight || undefined,
                  t: bodyType,
                }}
                className="btn-cta btn-cta-accent group inline-flex items-center justify-center gap-2 px-7 py-4 text-base font-semibold shadow-[0_20px_60px_-18px_color-mix(in_oklab,var(--accent)_60%,transparent)]"
              >
                Öppna hela verktyget
                <ArrowRight size={18} className="transition group-hover:translate-x-1" />
              </Link>
              <span className="inline-flex items-center gap-2 text-sm text-background/70">
                <Smartphone size={15} /> AR-läge, svängsimulering & planvy i skala
              </span>
            </div>
          </div>

          <div className="rounded-[28px] border border-background/15 bg-background/[0.06] p-5 backdrop-blur-sm md:p-6">
            <div
              className={`flex items-center gap-3 rounded-2xl border px-4 py-3.5 ${
                total.level === "ok"
                  ? "border-emerald-400/40 bg-emerald-400/10"
                  : total.level === "warn"
                    ? "border-amber-400/40 bg-amber-400/10"
                    : "border-red-400/40 bg-red-400/10"
              }`}
              aria-live="polite"
            >
              {ICON[total.level]}
              <span className="font-display text-lg leading-tight">{total.text}</span>
            </div>

            <div className="mt-4 text-background/90">
              <FitPlan dims={dims} site={site} />
            </div>

            <ul className="mt-4 space-y-2">
              {highlights.map((c) => (
                <li key={c.id} className="flex gap-2.5 text-sm text-background/75">
                  <span className="mt-0.5 shrink-0">{ICON[c.level]}</span>
                  <span>
                    <span className="font-medium text-background">{c.label}:</span> {c.detail}
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-4 text-xs text-background/55">
              {BODY_LABEL[bodyType]} · {fmt(dims.length)} m lång ·{" "}
              {mirrorWidth(dims).toFixed(2).replace(".", ",")} m med speglar · {fmt(dims.height)} m
              hög. Typiska mått – ange exakta i fullversionen.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
